import React, { useEffect, useRef, useState } from 'react'
import { fetchAPI, API_BASE_URL } from '@hpanel/web-common/api/client'

interface Snapshot {
  container_id: number
  container_name: string
  cpu_pct: number
  mem_used_mb: number
  mem_limit_mb: number
  disk_used_gb: number
  disk_limit_gb: number
  inodes_used: number
  bandwidth_in_bytes: number
  bandwidth_out_bytes: number
}

const POLL_MS = 5000
const RING_LEN = 30

function Sparkline({ samples, max }: { samples: number[]; max: number }) {
  const width = 120, height = 28
  if (samples.length === 0) return <svg width={width} height={height} />
  const step = width / Math.max(samples.length - 1, 1)
  const points = samples
    .map((v, i) => `${i * step},${height - (v / max) * height}`)
    .join(' ')
  return (
    <svg width={width} height={height} className="inline-block ml-2">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}

export const MetricsPanel: React.FC<{ containerId: number }> = ({ containerId }) => {
  const [snap, setSnap] = useState<Snapshot | null>(null)
  const [error, setError] = useState<string | null>(null)
  const cpuHistory = useRef<number[]>([])
  const memHistory = useRef<number[]>([])

  useEffect(() => {
    let cancelled = false
    const poll = async () => {
      try {
        const list = await fetchAPI<Snapshot[]>(`${API_BASE_URL}/metrics/me`)
        if (cancelled) return
        const mine = list.find(s => s.container_id === containerId)
        if (!mine) return
        setSnap(mine)
        cpuHistory.current = [...cpuHistory.current, mine.cpu_pct].slice(-RING_LEN)
        memHistory.current = [...memHistory.current, mine.mem_used_mb].slice(-RING_LEN)
        setError(null)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to read metrics')
      }
    }
    poll()
    const handle = setInterval(poll, POLL_MS)
    return () => { cancelled = true; clearInterval(handle) }
  }, [containerId])

  if (error) return <div data-testid="metrics-error" className="text-red-700">{error}</div>
  if (!snap) return <div data-testid="metrics-loading">Loading metrics…</div>

  const memPct = snap.mem_limit_mb > 0 ? (snap.mem_used_mb / snap.mem_limit_mb) * 100 : 0

  return (
    <div data-testid="metrics-panel" className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="border rounded p-4">
        <h3 className="font-semibold">CPU</h3>
        <p data-testid="metric-cpu" className="text-3xl">{snap.cpu_pct.toFixed(1)}%</p>
        <Sparkline samples={cpuHistory.current} max={100} />
      </div>
      <div className="border rounded p-4">
        <h3 className="font-semibold">Memory</h3>
        <p data-testid="metric-mem" className="text-3xl">
          {snap.mem_used_mb} MB / {snap.mem_limit_mb} MB ({memPct.toFixed(1)}%)
        </p>
        <Sparkline samples={memHistory.current} max={snap.mem_limit_mb || 1} />
      </div>
      <div className="border rounded p-4">
        <h3 className="font-semibold">Disk</h3>
        <p data-testid="metric-disk">{snap.disk_used_gb.toFixed(2)} GB / {snap.disk_limit_gb} GB</p>
      </div>
      <div className="border rounded p-4">
        <h3 className="font-semibold">Bandwidth</h3>
        <p data-testid="metric-bw">↓ {Math.round(snap.bandwidth_in_bytes / 1_048_576)} MB ↑ {Math.round(snap.bandwidth_out_bytes / 1_048_576)} MB</p>
      </div>
    </div>
  )
}
